/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type SelectOption, CAPACITY_UNIT, Consts, I18nService, UtilService } from '@dm/core';
import { StoragePoolAndLunWorkModeE, UNPLACEHOLDER } from 'app/common/constants';
import { SWITCH } from 'app/config';

export const SYSTEM_VSTORE_ID = '0';

export const CACHE_POLICY_INVALID_TIME_32BITS = '4294967295';

export const CACHE_POLICY_INVALID_TIME = '-1';

export const USAGE_TYPE = {
    common: 0,
    eDev: 1,
};

export const TAKEOVER_TYPE = {
    offline: 0,
    online: 1,
};

// LUN个数规格
export const MAX_LUN_NUMBER = {
    // 默认：500
    default: 500,
    // LUN组界面跳转：100
    fromLungroup: 100,
    // 微存储：8
    micro: 8,
    // 极简场景：100
    simpleMode: 100,
};

// LUN名称长度
export const PREFIX_MAX_LENGTH = {
    // 默认：255
    default: 255,
    // 后缀场景【255 - 4 = 251】
    prefix_model: 251,
    // 微存储直通微后缀加硬盘标记占8位长度【251 - 8 = 243】
    direct_micro: 243,
    // 微存储直通微后缀加硬盘标记占8位长度【255 - 8 = 247】--- 创建NS个数为一个的场景
    direct_micro_only_one: 247,
};

// 伪装类型
export const DISGUISE_STATUS = {
    none: 0,   // 不伪装
    basic: 1,  // 基本伪装
    extend: 2, // 扩展伪装
    third: 4,   // 第三方
};

/**
 * 查询映射类型
 */
export enum RelationType {
    LUN = 3,
    LUNGROUP = 2
}

/**
 * 映射目标对象类型
 */
export enum HostType {
    'HOST' = 'host',
    'HOST_GROUP' = 'hostGroup'
}
export enum WorkLoadCreateTypeE {
    BUILDIN = '0',  // 内置
    CUSTOM = '1' // 自定义
}

/**
 * 应用类型使用场景
 */
export enum WorkLoadUsageTypeE {
    ADVANCED = '0', // 标准模式
    SIMPLE = '1'  // 极简模式
}

/**
 * 应用类型极简模式场景值
 */
export enum LunWorkLoadSimpleModeTypeE {
    /** 块发放 */
    DEFAULT = '0',
    /** OLAP（线上分析处理） */
    COMMON_OLAP = '1033',
    /** OLTP（线上交易处理） */
    COMMON_OLTP = '1034',
    /** OLAP&OLTP（线上分析处理&线上交易处理） */
    COMMON_OLAP_OLTP = '1035',
    /** ERP（企业资源管理） */
    COMMON_ERP = '1036',
    /** VDI（虚拟桌面） */
    COMMON_VDI = '1037',
}

/**
 * 极简模式智能发放场景默认推荐LUN名称集合
 */
export const SimpleModeLunNameList = {
    /** Default（块发放） */
    [LunWorkLoadSimpleModeTypeE.DEFAULT]: [],
    /** OLAP（线上分析处理） */
    [LunWorkLoadSimpleModeTypeE.COMMON_OLAP]: [
        'Hive', 'SparkSQL', 'FlinkSQL', 'Clickhouse', 'Elasticsearch', 'Druid', 'Kylin', 'Presto', 'Impala',
    ],
    /** OLTP（线上交易处理） */
    [LunWorkLoadSimpleModeTypeE.COMMON_OLTP]: [
        'MySQL', 'PostgreSQL', 'Kingbase', 'Oracle', 'DB2', 'SQL_Server',
    ],
    /** OLAP&OLTP（线上分析处理&线上交易处理） */
    [LunWorkLoadSimpleModeTypeE.COMMON_OLAP_OLTP]: [],
    /** ERP（企业资源管理） */
    [LunWorkLoadSimpleModeTypeE.COMMON_ERP]: [
        'Yonyou', 'Kingdee', 'SAP',
    ],
    /** VDI（虚拟桌面） */
    [LunWorkLoadSimpleModeTypeE.COMMON_VDI]: [
        'VMware', 'Hyper-V', 'FusionCompute', 'H3C_CAS', 'Xenserver',
    ],
};

/**
 * LUN的空间类型
 */
export enum AllocTypeE {
    //thick lun
    THICK_LUN = '0',
    //thin lun
    THIN_LUN = '1'
}

/**
 * 元数据类型
 */
export const META_TYPE = 'createLUN';
export const META_TYPE_BATCH = 'createLUNBatch';

/**
 * 指定编号最大值
 */
export const MAX_STARTNUM = 10000;

export const LUN_AD_DEFAULT_DIGITS = 4;
export const LUN_AD_DEFAULT_SUFFIX = '0000';

export interface RemoveLunParams {
    // 定时HyperCDP计划ID
    ID: string;
    // 保护组ID
    ASSOCIATEOBJID: string;
    // LUN ID列表
    LUNIDARRAY: number[];
    vstoreId?: string;
}

// 高优先级缓存策略
export enum CachePolicyE {
    Normal = '0',
    High = '1'
}

// 高优先级缓存策略最大时间，单位天
export const CACHE_POLICY_MAX_DAYS = 7;

// 高优先级缓存策略最大时间，单位小时
export const CACHE_POLICY_MAX_HOURS = CACHE_POLICY_MAX_DAYS * 24;

// 高优先级缓存策略最小时间
export const CACHE_POLICY_MIN_HOURS = 1;

/**
 * 映射页面使用的SubType
 */
export enum SubTypeE {
    /** 普通LUN */
    NORMAL = 'normal',
    /** PE LUN */
    PE = 'pe',
    /** 通道LUN */
    CHANNEL = 'channel'
}

/**
 * 极简模式LUN详情概览编辑项
 */
export enum EditType {
    /** 容量阈值 */
    ALARM_THRESHOLD = 'alarmThreshold',
    /** 描述 */
    DESCRIPTION = 'description'
}

/**
 * 创建LUN组任务名称替换
 */
export enum TaskNameE {
    /** LUN组增加LUN 不需要保护组字样 */
    addLunToLunGroup = 'addLunToLunGroup',
    /** LUN组移除LUN 不需要保护组字样 */
    removeLunFromLunGroup = 'removeLunFromLunGroup'
}

/**
 * 创建LUN 参数类型
 */
export interface CreateLunParams {
    storagePoolID: string;
    alarmCheck: boolean;
    alarmThreshold: boolean;
    ALLOCTYPE: string;
    workloadType: string;
    cachePolicy: string;
    highPriorityValidTime: string;
    dataAffinityMode?: StoragePoolAndLunWorkModeE;
    OWNINGCONTROLLER?: string;
}

/**
 * 映射 参数类型
 */
export interface MappingInfo {
    hostID: string;
    hostGroupID: string;
    portGroupID: string;
    hostLunIdStart?: string;
}

/**
 * 创建LUN组 参数类型
 */
export interface CreateLunGroupParams {
    name: string;
    description: string;
    newLunsInfo: CreateLunParams[];
    existLunIDs: string[];
    mappedInfo: MappingInfo;
    protectGroupName: string;
    force: boolean;
    task_name?: string;
    vstoreId?: string;
}

/**
 * LUN组移除LUN 参数类型
 */
export interface LgPgRemoveMemberLunParams {
    lunGroupId: string;
    lunIDs: string[];
    isDelay: boolean;
    task_name?: string;
}

@Injectable()
export class LunsConst {
    /** 映射类型 */
    mappingTypeStr = {
        0: this.get('common.mapping_unMapped_value'),
        1: this.get('common.host_title'),
        2: this.get('common.hostGroup_title'),
        3: this.get('common.objectType_LUN'),
        4: this.get('common.lungroup_checkbox'),
    };

    /** LUN的子类型 */
    SUB_TYPE = {
        /** 普通LUN */
        LUN: '0',
        /** VVol LUN */
        VVOL: '1',
        /** PE LUN */
        PE: '2',
        // 通道LUN
        CHANNEL: '7',
    };
    /** LUN的使用范围 */
    USAGE_TYPE = {
        /** 普通LUN */
        LUN: '0',
        /** 第三方阵列创建的LUN */
        EDEV: '1',
        /** VVol LUN */
        VVOL: '2',
        /** PE LUN */
        PE: '3',
        /** 容器LUN */
        GLOBAL: '5',
        LOCAL: '6',
        CONTAINER: '7',
        /** 通道LUN */
        CHANNEL: '10',
    };

    // 选中对象最大个数
    selectMaxObjectNum = 100;

    common = {
        lunDetailTitle: this.get('home.lun_detail_title'),
        lunGroupDetailTitle: this.get('home.lunGroup_detail_title'),
        foldBtn: this.get('settings.upgrade_task_fold_btn'),
        unfoldBtn: this.get('settings.upgrade_task_unfold_btn'),
        noPoolMessageTip1: this.get('storage.lun_task_create_noPool_info1'),
        noPoolMessageTip2: this.get('storage.storagePool_create_task_title_wintitle'),
        noPoolMessageTip3: this.get('storage.lun_task_create_noPool_info2'),
        noObjectInfo: this.get('common.noObject_info'),
        moreBtn: this.get('common.more_button'),
        diffVstoreSelectTip: this.get('storage.lun_task_selectSameVstore_tip'),
        selectVstoreTitle: this.get('common.EnumDefine_EVENT_LEVEL_E_INFO'),
        selectVstoreFirst: this.get('service.share_term_create_chooseVstore_tip'),
        vstoreLabel: this.get('common.owningVstore_lab'),
        rmVstoreLabel: this.get('common.remoteVstore_lab'),
        vstoreName: this.get('vstore_name_lab'),
        vstoreId: this.get('vstore_id_lab'),
        notSupportExpandRemote: this.get('storage.lun_task_expand_configRemoteProtection_tip'),

        createBtn: this.get('common.create_button'),
        expandBtn: this.get('common.expand_label'),
        modifyBtn: this.get('common.modify_button'),
        copyBtn: this.get('common.copy_button'),
        modifyLunGroup: this.get('storage.LunGroup_task_modify_title'),
        modifyLunGroupMsg: this.get('rest.modify_lunGroup_msg1'),

        deleteLunGroup: this.get('storage.LunGroup_task_delete_title'),
        deleteBtn: this.get('common.delete_button'),
        confirmBtn: this.get('common.confirm_button'),
        closeBtn: this.get('common.close_button'),
        cancelBtn: this.get('common.cancel_button'),
        addLunBtn: this.get('storage.lunGroup_term_addLun_lab'),
        addLunMsg: this.get('rest.add_lun_msg'),
        addBtn: this.get('common.add_button'),
        removeBtn: this.get('common.remove_button'),
        removeLunBtn: this.get('storage.lunGroup_term_removeLUN_lab'),
        removeLunMsg: this.get('rest.remove_lun_msg'),
        createLunGroupMsg: this.get('rest.createA_lunGroup_msg'),
        createLunsMsg: this.get('rest.batchCreateLUN_msg'),
        addNewLunsToLunGroupMsg: this.get('rest.batchAddNewLUNtoCG_msg'),
        addExistingLunsToLunGroupMsg: this.get('rest.batchAddExistLUNtoCG_msg'),
        // 通知定时HyperCDP计划
        notifyCdp: this.get('monitor.taskCenter_notice_hyperCDP_plan_steps'),
        deleteLGMsg: this.get('rest.delete_lunGroup_msg1'),
        mappingBtn: this.get('common.map_button'),
        mappingsLab: this.get('storage.LUN_task_view_mappings_tab'),
        unmapBtn: this.get('host.hostGroup_task_cancleMap_lab'),
        restoreBtn: this.get('storage.recycleBin_task_restore_btn'),
        clearBtn: this.get('common.clear_button'),
        changePool: 'Change Pool',
        advancedLab: this.get('advance_label'),
        advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
        // 提示范围 x~x
        limit_label: this.get('common_limit_label'),
        // 所有
        allValue: this.get('common.all_value'),
        // None
        none: this.get('common.term_confirm_none'),
        // 名称
        nameLab: this.get('common.name_column'),
        // 名称前缀
        namePrefixLab: this.get('storage.smartMigration_task_create_namePrefix_lab'),
        // 描述
        descriptionLab: this.get('common.description_column'),
        // 状态
        statusLab: this.get('common.status_column'),
        // 健康状态
        healthStatusLab: this.get('common.healthStatus_column'),
        // 运行状态
        runningStatusLab: this.get('common.runningStatus_column'),
        // 容量告警
        alarmCheck: this.get('storage.lun_task_create_capAlarm_col'),
        // 数量
        numberLab: this.get('common.quantity_label'),
        // 单盘Namespace数量
        singleDiskNumberLab: this.get('storage.lun_task_create_fsNumOnePool_lab'),
        // 容量
        capacityLab: this.get('common.capacity_column'),
        // IP地址
        ipLab: this.get('common.IPaddress_column'),
        // 数据缩减比
        TotalSavedRatioLab: this.get('storage.dashboad_term_dataCompressionRate_label'),
        // 单LUN容量
        capacityPerLunLab: this.get('common.CapacityperLUN_column'),
        // 总容量
        totalCapacityLab: this.get('common.totalCapacity_lab'),
        // 容量利用率
        capUsageLab: this.get('common.capacityUsage_column'),
        // 请选择
        pleaseSelect: this.get('common.select_lab'),
        // 已分配容量
        alloccCapacityLab: this.get('common.allocatedCapacity_short_lab'),
        // 已用容量占比
        usedCapacityRatioLab: this.get('service.fileSystem_term_usedCapaRetio_tab'),
        // 数据保护容量
        replicationCapacityLab: this.get('common.dataProtection_title'),
        dataProtectionCapacityLab: this.get('storage.storagePool_term_dataProtectionCapacity_column'),
        dataProtectionLab: this.get('storage.storagePool_term_dataProtection_column'),
        snapReserveCapacityLab: this.get('service.filesystem_view_task_general_para_snapReserveCapacity_label'),
        snapUsedCapacityLab: this.get('service.filesystem_view_task_general_para_snapUsedCapacity_label'),
        capacityThresholdLab: this.get('service.filesystem_term_consumedCapThreshold_lab'),
        // 快照空间比例
        snapshotRate: this.get('service.filesystem_term_snapSpaceRatio_noUnit_lab'),
        // 容量告警阈值
        alarmThresholdLab: this.get('storage.storagePool_create_task_para_consumedCapThreshold_lab1'),
        alarmThreshold: this.get('storage.storagePool_create_task_para_consumedCapThreshold_label'),
        cdpScheduleLab: this.get('storage.lun_task_addToHyperCDPScheduley_HyperCDPSchedule_label'),
        // 信息汇总
        summaryLab: this.get('common.task_collectInfo_title_label'),
        // 拓扑图
        topologyLab: this.get('storage.lunGroup_view_topology_lab'),
        topologySLab: this.get('storage.lun_simplifiedMode_topology_lab'),
        hostNumInfo: (num: number): string => this.get('home.search_term_hostsNum_tip', [num]),
        backPortNumInfo: (num: number): string => this.get('storage.lun_simplifiedMode_backEndPortsNum_info', [num]),
        diskNumInfo: (num: number): string => this.get('storage.lun_simplifiedMode_diskNum_info', [num]),
        abnormalInfo: this.get('common.EnumDefine_STATUS_E_ABNORMAL'),
        topologyHostErrInfo: (num: number): string => this.get('storage.lun_simplifiedMode_info',
            [num, this.common.abnormalInfo, this.common.hostsLab]).toLowerCase(),
        hostsLab: this.get('common.Hosts'),
        topologyHostOfflineInfo: (num: number): string => this.get('storage.lun_simplifiedMode_info',
            [num, this.common.offlineStatusInfo, this.common.hostsLab]).toLowerCase(),
        offlineStatusInfo: this.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
        portsLab: this.get('common.Ports'),
        topologyPortErrInfo: (num: number): string => this.get('storage.lun_simplifiedMode_info',
            [num, this.common.abnormalInfo, this.common.portsLab]).toLowerCase(),
        backPortsLab: this.get('monitor.charts_term_objectType_BACKPORTS'),
        topologyPortOfflineInfo: (num: number): string => this.get('storage.lun_simplifiedMode_errPort_info',
            [num, this.common.offlineStatusInfo, this.common.backPortsLab]).toLowerCase(),
        topologyDiskErrInfo: (num: number): string => this.get('storage.lun_simplifiedMode_disk_info',
            [num, this.common.abnormalInfo]).toLowerCase(),
        topologyDiskOfflineInfo: (num: number): string =>
            this.get('storage.lun_simplifiedMode_disk_info', [num, this.common.offlineStatusInfo]),
        // 映射关系
        mappingLab: this.get('storage.LUN_task_view_mapping_tab'),
        mapBtnLab: this.get('common.map_button'),
        protectLab: this.get('common.protection_title'),
        childrensLab: this.get('storage.recycleBin_task_childrens_tab'),
        unusedRawCapacityLabel: this.get('storage.dashboard_term_CapacityInfo_UnusedRawCapacity_label'),
        runningStatusNormal: this.get('common.EnumDefine_RUNNING_STATUS_E_HyperCopy_NORMAL'),
        runningStatusError: this.get('common.EnumDefine_RUNNING_STATUS_E_ERROR'),
        // Action
        actionLab: this.get('common.operation_button'),
        // 概览
        overviewLab: this.get('home.AAOverview_term_AAOverview_tittle'),
        lunGroup: this.get('common.lunGroup_title'),
        host: this.get('common.host_title'),
        hostGroup: this.get('common.hostGroup_list_title'),
        /**
         * 主机：
         */
        hostWithColon: this.get('common.host_title', true),
        /**
         * 主机组：
         */
        hostGroupWithColon: this.get('common.hostGroup_list_title', true),
        owningHostGroupLab: this.get('host.host_view_summary_belongedHostGroup_label'),
        lun: this.get('common.lun_title'),
        enableLab: this.get('common.enable_label'),
        enabledLab: this.get('common.enabled_label'),
        enabled0Lab: this.get('common.service_enable_value'),
        disableLab: this.get('common.disable_label'),
        disabledLab: this.get('common.disabled_label'),
        // WWN
        wwn: this.get('storage.lun_view_summary_localWWN_label'),
        nguid: this.get('storage.LUN_task_view_NGUID_lab'),
        lunID: this.get('host.host_task_mappingLun_hostLunID_lab'),
        automatic: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        manually: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        mapping: this.get('common.mapping_column'),
        mapped: this.get('common.mapping_mapped_value'),
        unmapped: this.get('common.mapping_unMapped_value'),
        viewLunId: this.get('host.host_view_toplogy_viewHostLunID_button'),
        modifyLunId: this.get('host.host_view_toplogy_modifyLunID_title'),
        mappingType: this.get('host.host_view_toplogy_mappingType_lab'),
        protect: this.get('storage.lunGroup_term_protect_button'),
        success: this.get('monitor.task_status_result_success'),
        modifyLun: this.get('storage.lun_task_modifyLUN_title'),

        // 修改LUN缓存策略为性能层场景有高危提示
        modifyHighCachePolicyTip: this.get('storage.lun_task_modify_cachePolicy_confirm_msg'),

        replicationPair: this.get('protection.replicationCG_task_create_remoteRepliPair_lab'),
        snapshot: this.get('common.snapshot_title'),
        snapshotNum: this.get('storage.lun_simplifiedMode_snapshotNum_lab'),
        clone: this.get('common.clone_button'),
        hyperMetroPair: this.get('protection.hyperMetroCG_task_create_hyperMetroPair_label'),
        hyperCDP: this.get('protection.HyperCDP_title'),
        replication: this.get('common.replication_title'),
        hyperMetro: this.get('common.HyperMetro_title'),
        cloudBackup: this.get('common.cloudBackup_title'),
        drStar: this.get('protection.dataProtection_term_drStar_featureName'),
        expandLun: this.get('storage.lun_task_expandLUN_title'),
        noData: this.get('monitor.recentTasks_result_noData'),
        addPortGroupBtn: this.get('storage.LunGroup_view_addPortGroup_button'),
        addPortGroupMsg: this.get('rest.addPortGroup_msg'),
        removePortGroupBtn: this.get('storage.LunGroup_view_removePortGroup_button'),
        viewLab: this.get('common.view_value'),
        // 修改LUN组，主机，端口组之间映射关系
        addPortGroupTipsForHost: this.get('storage.lunGroup_task_modifyMap_toHost_HostConGuide_msg'),
        removePortGroupTipsForHost: this.get('storage.lunGroup_task_modifyMap_toHost_HostConGuide_msg'),
        // 修改LUN组，主机组，端口组之间映射关系
        addPortGroupTipsForHostGroup: this.get('storage.lunGroup_task_modifyMap_toHostGroup_HostConGuide_msg'),
        removePortGroupTipsForHostGroup: this.get('storage.lunGroup_task_modifyMap_toHostGroup_HostConGuide_msg'),
        // 修改LUN，主机，端口组之间映射关系
        modifyMapBetweenLunHostPortG: this.get('storage.lun_task_modifyMap_toHost_HostConGuide_msg'),
        removePortGroupTipsForLunHost: this.get('storage.lun_task_modifyMap_toHost_HostConGuide_msg'),
        unmapLun2HostTips: this.get('storage.lun_task_cancleMap_toHost_msg'),
        lunTaskCreateMappedPortGroupInfoLab: this.get('storage.Lun_task_create_mappedPortGroup_info_lab'),
        hostLunIdLab: this.get('host.host_task_mappingLun_startID_lab'),
        unmapSuccess: this.get('rest.removeMapping_success_msg'),
        selectOneObjectErr: this.get('common.selectOneObject_lab'),
        selectMaxObjectErr: this.get('common.selectedObjectNum_max_tip'),

        unassoicateLab: this.get('storage.lun_createLunGroup_task_option_showNOOwningLUN_value_label'),
        noLunTip: this.get('storage.smartQoS_task_removeLun_noLun_tip'),
        unMapTip: this.get('common.LUNGroup_task_unmap_error_tips'),
        deleteTip: this.get('common.LUNGroup_task_delete_error_tips'),
        selectOneObjectTip: this.get('common.selectOnlyOneObject_lab'),
        addPortGroupTip: this.get('common.LUNGroup_task_topology_addPort_error_tips'),
        removePortGroupTip: this.get('common.LUNGroup_task_topology_removePort_error_tips'),
        unMapLunTip: this.get('common.LUN_task_unmap_belongToLUNGroup_error_tips'),
        lunGroupUnavailablePortGroupTip: this.get('common.LUNGroup_task_unavailablePortGroup_error_tips'),
        basicInfoLab: this.get('common.basicInfo_title'),
        createTypeLab: this.get('common.SmartRecommend_createType_lab'),
        fourceHostLunIDSameLab: this.get('host.host_task_mappingLun_fourceHostLunIDSame_lab'),
        fourceHostLunIDSametTip: this.get('host.host_task_mappingLun_fourceHostLunIDSame_tip'),
        preTaskFail: this.get('task_error_preTaskFail_message'),
        lunGroupName: this.get('host.host_task_mapping_lunGroupName_lab'),
        protectionGroupAddLUNErrMsg: this.get('protection.protectionGroup_task_addLUN_err_msg'),

        deletePassthroughModeMsg: this.get('storage.Namespace_delete_PassthroughMode_msg'),
        // 创建时间
        createTime: this.get('common.createTime_label'),
        smartRecommendDataTimeoutMsg: this.get('rest.getSmartRecommendDataTimeout_msg'),
        idLabel: this.get('common.ID_column'),
        lunCreateGuide: this.get('home.lun_create_desc_tip'),

        // 创建LUN 容量block场景展示信息
        capacityShowInfoFn: (blockValue: string, sectorValue: string): string =>
            this.get('storage.lun_task_create_capacity_blocks_msg', [blockValue || 0, sectorValue || 0]),
        workModeLab: this.get('storage.storagePool_term_affinityMode_label'),
        workModeHelpTip: this.get('storage.lun_term_workMode_tip'),
        devControllerHelpTip: this.get('storage.lun_term_ownedController_tip'),
        aaModeLab: this.get('storage.storagePool_term_workMode_aa_value'),
        apModeLab: this.get('storage.storagePool_term_workMode_ap_value'),
        devControllerLab: this.get('service.fileSystem_task_view_ownedController_lab'),
        defaultLab: this.get('common.default_label'),
    };

    /**
     * LUN工作模式选项
     */
    workModeOpts: SelectOption[] = [
        {
            label: this.common.aaModeLab,
            value: StoragePoolAndLunWorkModeE.AA_MODE,
        },
        {
            label: this.common.apModeLab,
            value: StoragePoolAndLunWorkModeE.AP_MODE,
        },
    ];

    /**
     * 归属所有控制器（选项）
     */
    ownedByAllController: SelectOption = {
        label: this.common.defaultLab,
        value: 'all',
    };

    // 映射状态图标
    mappedSvg = {
        true: 'mapped',
        false: 'unmapped',
    };

    // 健康状态图标
    healthStatusSvg = {
        1: 'normal',
        2: 'faulty',
        15: 'write-protected',
    };

    usageType = {
        0: this.get('common.EnumDefine_LUN_USAGE_TYPE_E_Internal'),
        1: this.get('common.EnumDefine_LUN_USAGE_TYPE_E_External'),
        /** 通道LUN */
        10: this.get('storage.channelLUN_task_useType_value'),
    };

    functionType = {
        1: this.get('common.objectType_LUN'),
        2: this.get('common.snapshot_title'),
        3: this.get('common.clone_button'),
    };

    alarmCheck = {
        true: this.get('common.enabled_switch_column'),
        false: this.get('common.disabled_switch_column'),
    };

    takeoverType = {
        online: this.get('common.EnumDefine_RUNNING_STATUS_E_ONLINE'),
        offline: this.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
    };

    takeoverTypeOptions = [
        {
            label: this.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
            value: 0,
        },
        {
            label: this.get('common.EnumDefine_RUNNING_STATUS_E_ONLINE'),
            value: 1,
        },
    ];

    cachePolicyObj = {
        [CachePolicyE.Normal]: this.get('storage.Namespace_create_cachePolciy_normal_value'),
        [CachePolicyE.High]: this.get('storage.Namespace_create_cachePolicy_high_value'),
    };

    lunGroup = {
        winTitle: this.get('storage.LunGroup_task_create_title'),
        poolTitle: this.get('storage.LunGroup_term_belongedStoragePool_label'),
        diskTitle: this.get('storage.lun_task_create_belongPool_lab'),
        diskCapInfo: this.get('storage.lun_task_create_fsCap_info'),
        poolTip: this.get('common.storagePool_label'),
        newLun: this.get('storage.lunGroup_task_create_newLun_lab'),
        exsistLun: this.get('storage.lunGroup_task_create_createdLun_lab'),
        hostLab: this.get('storage.LunGroup_task_create_mapTo_lab'),
        mapToHost: this.get('storage.LunGroup_task_create_mappingHost_lab'),
        portGroupLab: this.get('common.portGroup_title'),
        LunsTitle: this.get('common.LUNs', true),
        // 移除成员 ID
        removeObjId: this.get('storage.lunGroup_task_delete_result_removeMember_lab', [this.get('common.ID_column')], true),
        memberLuns: this.get('storage.lunGroup_view_menberLUN_lab'),
        lunNum: this.get('storage.LunGroup_term_LUNCount_column'),
        // LUN组映射主机组高危
        // eslint-disable-next-line max-len
        lunGrpToHostGrpTip: (lunGroupName, hostGroupName): string => this.get('storage.lunGroup_task_map_toHostGroup_msg', [lunGroupName, hostGroupName]),
        // LUN组映射主机高危
        lunGrpToHostTip: (lunGroupName, hostName): string => this.get('storage.lunGroup_task_map_toHost_msg', [lunGroupName, hostName]),
        delayExecute: this.get('protection.isDelay_label'),
        delayExecuteTipHyperMetroRemove: this.get('protection.isDelay_hyperMetro_remove_msg'),
        deleteRemoteLuns: this.get('storage.LunGroup_task_removeRemoteLUN_checkbox'),
        deleteRemoteLunsTip: this.get('storage.LunGroup_task_removeRemoteLUN_tip'),
        deleteErrorForExistPg: (pgName): string => this.get('rest.deletePGbelongLUNGroup_msg', [pgName]),
        lunGroupNameLab: this.get('host.host_task_mapping_lunGroupName_lab'),
        addtoLunGroupTitle: this.get('storage.lun_task_create_addtoLunGroup_title'),
    };

    lun = {
        // 创建LUN
        CreateWinTitle: this.get('storage.lun_task_create_title'),
        CreateLUNTitle: this.get('storage.lun_task_create_title'),
        crateLunMsg: this.get('rest.create_lun_msg'),
        crateLunMsgFn: (value): string => this.get('rest.create_lun_msg', [value]),
        startIDTitle: this.get('host.host_task_mappingLun_startID_lab'),

        // 起始编号
        startNoLab: this.get('storage.lun_task_create_startNumber_lab'),
        startNoTips: this.get(SWITCH.IS_DIRECT_MODE ?
            'storage.lun_task_create_quantity_direct_description_tip' : 'storage.lun_task_create_quantity_description_tip'),
        minCapErr: this.get('storage.lun_task_create_capacity_range_EqualOrBigger_msg'),
        maxCapErr: this.get('storage.lun_task_create_capacity_range_smallerOrEqual_msg', [256]),
        maxCapCommonErr: this.get('storage.lun_task_create_capacity_range_msg', [256]),
        noDiskTip: this.get('storage.storagePool_expand_task_noValidController_msg'),
        capHelpTips: this.get('common.capacity_normalstatu_msg'),
        numberRange: this.get('common.valueRange_label', [1, 500]),
        startNumberRange: (from, to): string => this.get('common.valueRange_label', [from, to]),
        alarmThresholdNumberRange: this.get('common.valueRange_label', [50, 99]),
        dedup: this.get('storage.lun_task_create_smartDedupe_label'),
        compression: this.get('storage.lun_task_create_smartCompression_label'),
        dataReductionLab: this.get('storage.dataReduction_lab'),
        wwnLab: this.get('storage.lun_view_summary_localWWN_label'),
        lunCountLab: this.get('common.LunCount_label'),
        nguidLab: this.get('storage.LUN_task_view_NGUID_lab'),
        poolLab: this.get('storage.LunGroup_term_belongedStoragePool_label'),
        diskLab: this.get('storage.lun_task_create_belongPool_lab'),
        lunGroupLab: this.get('storage.lun_view_summary_belongedLunGroup_tab'),
        owningLunGroupsLab: this.get('storage.lun_view_summary_belongedLunGroups_title'),
        requestSizeLab: this.get('storage.lun_task_create_appRequestSize_label'),
        requestSizeInfoLab: this.get('storage.lun_task_create_appRequestSize_label', true),
        recommendationLab: this.get('storage.Namespace_create_recommendScenario_lable', true),
        typeLab: this.get('common.type_column', true),
        compressionLab: this.get('storage.lun_task_create_smartCompression_label', true),
        dedupLab: this.get('storage.lun_task_create_smartDedupe_label', true),
        compressionPropertyLab: this.get('storage.lun_task_create_smartCompression_label'),
        dedupPropertyLab: this.get('storage.lun_task_create_smartDedupe_label'),
        workloadTypeLab: this.get('storage.lun_term_appType_label'),
        protectionGroupLab: 'Owning Protection Group',
        //数据缩减开关
        dataReductionSwitchLab: this.get('storage.dataReduction_lab'),
        // 启用
        enable: this.get('common.enable_label'),
        // 写策略类型
        writePolicyTypeLab: this.get('storage.lun_task_create_writePolicyType_label'),
        // 可选LUN
        optionsLun: this.get('common.availableLun_column'),
        // 已选LUN
        selectedLun: this.get('common.selectedLun_column'),
        // 已增加的LUN
        addedLun: this.get('storage.lunGroup_view_opration_removeLUN_addedLUN_lab'),
        // 待移除的LUN
        waitingRemoveLun: this.get('storage.lunGroup_view_opration_removeLUN_waitRemovedLUN_lab'),
        // 删除LUN
        deleteLun: this.get('storage.lun_task_delete_title'),
        deleteLunMsg: this.get('rest.deleteLun_msg1'),
        deleteLunsMsg: this.get('storage.lun_task_batchDelete_msg'),
        deleteChannelLunContent: this.get('storage.channelLUN_task_delete_confirm_msg'),
        protection: this.get('common.dataProtection_title'),

        // 当前容量
        currentCap: this.get('common.currentCapacity_label'),
        // 扩容容量
        expandCap: this.get('storage.lun_task_expand_capacity_label'),
        // 扩容后容量
        afterExpandCap: this.get('storage.lun_task_expand_capacityAfterExapnded_label'),
        usageType: this.get('common.Types_Of_Use'),
        takeoverType: this.get('common.Takeover_Type'),
        remoteWWN: this.get('storage.LunGroup_term_remoteWWN_label'),
        takeoverLUNWWN: this.get('storage.LunGroup_term_takeOverLUNWWN_label'),
        takeoverLab: this.get('common.Takeover'),
        expandEdevTip: this.get('common.lun_expand_task_para_capcity_postcondition_tip'),
        expandReplicaLunTip: this.get('storage.lun_task_expand_expandReplicaLunTip'),

        mappingPeLunTitle: this.get('storage.vvol_task_mappingPELUN_tittle'),
        functionType: this.get('storage.LUN_task_view_fuctionType_column'),
        smartCachePartitionLab: this.get('storage.smartCache_task_createPool_smartCachePartition_lab'),
        addLunToPartition: (lun, partition): string => this.get('rest.addLUNtoSmartCachePartition_msg', [lun, partition]),
        removeLunToPartition: (lun, partition): string => this.get('rest.removeLUNfromSmartCachePartition_msg', [partition, lun]),
        modifyLunMsg: (lun): string => this.get('rest.modifyLun_msg', [lun]),
        addtoLunGroupLab: this.get('storage.lun_task_create_addtoLunGroup_lab'),
        belongedLunGroupInfoLab: this.get('storage.lun_task_create_belongedLunGroup_info_lab'),
        belongedLunGroupErrTip: this.get('storage.lun_task_create_belongedLunGroup_err_tip'),
        // 后缀编号
        suffixNumLab: this.get('storage.lun_task_create_suffixNumber_lab'),
        // 编号位数
        digitsLab: this.get('storage.lun_task_create_numberCharacter_lab'),
        // 到
        rangeLab: this.get('common.range'),
        // 通道LUN
        channelLunLab: this.get('storage.channelLUN_task_title'),
        createChannelLunTitle: this.get('storage.channelLUN_task_create_title'),
        modifyChannelLunTitle: this.get('storage.channelLUN_task_modify_title'),
        deleteChannelLunTitle: this.get('storage.channelLUN_task_delete_title'),
        mappingChannelLunTitle: this.get('storage.channelLUN_task_map_title'),
        unMappingChannelLunTitle: this.get('storage.channelLUN_task_unmap_title'),
        channelUsageType: this.get('storage.channelLUN_task_useType_value'),
        cachePolicy: this.get('storage.Namespace_create_cachePolicy_lable'),
        cachePolicyTip: this.get('storage.Namespace_create_cachePolicy_tip'),
        cachePolicyNormal: this.get('storage.Namespace_create_cachePolciy_normal_value'),
        cachePolicyHigh: this.get('storage.Namespace_create_cachePolicy_high_value'),
        cachePolicyValidTimeSwitchLab: this.get('storage.Namespace_create_cachePolciy_setValidationTime_lable'),
        highPriorityValidTime: this.get('settings.securityPolicy_task_username_validationTime_lab'),
        highPriorityValidTimeTip: this.get('common.range_validation', [
            this.get('settings.securityPolicy_task_username_validationTime_lab'),
            this.get('common.validity_hours_value', [CACHE_POLICY_MIN_HOURS]),
            this.get('common.validity_day_value', [CACHE_POLICY_MAX_DAYS]),
        ]),
        days: this.get('common.lower_days_label'),
        hours: this.get('common.timeAcount_hours_label'),
        numberTipsForChannel: this.get('storage.channelLUN_task_create_quantity_description_tip'),
        mapChannelLunContent: (hostName: string): string => this.get('storage.channelLUN_task_map_confirm_msg', [hostName]),
        unmapChannelLunContent: this.get('storage.channelLUN_task_unmap_confirm_msg'),
        //空间类型
        alloctypeLab: this.get('storage.lun_task_create_spaceType_lab'),
        //空间类型参数说明
        alloctypeTip: this.get('storage.lun_task_create_spaceType_tip'),
        //thin LUN
        alloctypeThin: this.get('storage.lun_task_create_spaceType_thinLUN_value'),
        //thick LUN
        alloctypeThick: this.get('storage.lun_task_create_spaceType_thickLUN_value'),
        //创建Thick LUN高危提示
        createThickLunTip: this.get('storage.lun_task_createThickLUN_confirm_msg'),
        // 创建LUN缓存策略性能层场景有高危提示
        createHighCachePolicyTip: this.get('storage.lun_task_create_cachePolicy_confirm_msg'),
        // 创建通道lun主机提示
        createChannelLunHostTip: this.get('storage.lun_task_create_channelMappingHost_tip'),
        // 容器存储title
        containerStorageLab: this.get('storage.containerStorage_task_title'),
        cdpEnableSwitchLabel: this.get('protection.protection_simplifiedMode_ScheduledSnapSwitch_col'),
    };

    protect = {
        createDrStar: this.get('protection.dataProtection_term_drStar_create_button'),
        createHyperMetroCg: this.get('protection.protectionGroup_task_createHyperMetro_button'),
        createRelicationCg: this.get('protection.protectionGroup_createReplication_button'),
        repCgNumErrMsg: this.get('protection.replicationCG_term_ofLUNGroup_getToUPLimit_msg'),
        createClone: this.get('protection.clone_task_createClone_button'),
        createHyperMetroPair: this.get('protection.protectionGroup_task_createHyperMetro_button'),
        createRelicationPair: this.get('protection.protectionGroup_createReplication_button'),
        createSnap: this.get('protection.protectionGroup_createSnapshot_button'),
        createCdp: this.get('storage.lun_task_createHyperCDP_button'),
        addToCdpPlan: this.get('storage.lun_task_addtoHyperCDPSchedule_button'),
        edevLunTips: this.get('common.lun_term_edevLUN_noProctect_error_tip'),
    };

    // 容量单位下拉列表
    capUnitOpts = [
        {
            label: 'TB',
            value: Consts.TB,
        },
        {
            label: 'GB',
            value: Consts.GB,
        },
        {
            label: 'MB',
            value: Consts.MB,
        },
        {
            label: 'Blocks',
            value: Consts.SECTOR,
        },
    ];

    healthStatusOpts = {
        1: this.get('common.EnumDefine_STATUS_E_NORMAL'),
        2: this.get('common.EnumDefine_HEALTH_STATUS_fault'),
        15: this.get('common.EnumDefine_HEALTH_STATUS_E_WRITE_PROTECT'),
    };

    runningStatusOpts = {
        27: this.get('common.EnumDefine_RUNNING_STATUS_E_ONLINE'),
        28: this.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
    };

    hostTypeOpts = [
        {
            label: this.common.hostGroup,
            value: 'hostGroup',
        },
        {
            label: this.common.host,
            value: 'host',
        },
    ];

    // 写策略类型
    writePolicyE = {
        Back: {
            value: 1,
            i18n: this.get('storage.lun_task_create_writeBack_label'),
        },
        Through: {
            value: 2,
            i18n: this.get('storage.lun_task_create_writeThrough_label'),
        },
    };

    // 映射状态
    mappingOpts = {
        true: this.common.mapped,
        false: this.common.unmapped,
    };

    topoViewTypeText = {
        topo: this.i18n.get('host.hostGroup_view_topology_tableView_lab'),
        table: this.i18n.get('common.topology_lab'),
    };

    // topo图
    topo = {

    };

    // "0"=内置，"1"=自定义
    workloadCreateTypeMap = {
        0: this.i18n.get('common.buildin_value'),
        1: this.i18n.get('common.custom_value'),
    };

    // "0" 云/互联网，"1" HPC/AI场景
    recommendScenarioMap = {
        0: this.i18n.get('storage.Namespace_create_recommendScenario_cloudInternet_value'),
        1: this.i18n.get('storage.Namespace_create_recommendScenario_HPCAI_value'),
    };

    smartgui = {
        recDes: this.i18n.get('common.common_term_SmartRecommend_des_lab'),
    };

    // 自定义编号位数
    digitsOpt = [
        {
            label: '1',
            value: 1,
        },
        {
            label: '2',
            value: 2,
        },
        {
            label: '3',
            value: 3,
        },
        {
            label: '4',
            value: 4,
        },
    ];

    //空间类型
    alloctypeOpts = {
        [AllocTypeE.THIN_LUN]: this.get('storage.lun_task_create_spaceType_thinLUN_value'),
        [AllocTypeE.THICK_LUN]: this.get('storage.lun_task_create_spaceType_thickLUN_value'),
    };

    /**
     * lun CDP计划创建快照开关label
     */
    lunCdpEnableSwitchLabel = {
        true: this.get('common.enabled_switch_column'),
        false: this.get('common.service_disable_value'),
    };

    constructor(
        private i18n: I18nService,
        private utilService: UtilService,
    ) { }

    get(key, params?, colon?): string {
        return this.i18n.get(key, params, colon);
    }

    renderData = (data): any => {
        const randeredData = {
            ...data,
            NAME: data.NAME,
        };
        return randeredData;
    };

    getTakeoverType(value, isEDev): string {
        // 离线接管；不返回（不伪装）
        // 在线接管：1（基本伪装）2（扩展伪装）4（第三方）
        let takeoverType: string;

        if ([DISGUISE_STATUS.basic, DISGUISE_STATUS.extend, DISGUISE_STATUS.third].includes(Number(value))) {
            takeoverType = this.takeoverType.online;
        } else {
            if (isEDev) {
                takeoverType = this.takeoverType.offline;
            } else {
                takeoverType = UNPLACEHOLDER;
            }
        }

        return takeoverType;
    }

    /**
     * 取值范围提示
     * @param min
     * @param max
     */
    rangeTip = (min: number, max: number): string => this.get('common.valueRange_label', [min, max]);

    /**
     *  自适应计算容量单位
     *  @param capacity 容量（SECTOR）
     */
    getValueAndUnit = (capacity: number): any => {
        if (capacity >= 2048) {
            return this.utilService.adaptUnit(Number(capacity), Consts.SECTOR);
        }
        // 容量低于1MB时用Blocks作为单位
        return {
            value: capacity,
            unit: Consts.SECTOR,
            label: `${capacity} Blocks`,
        };
    };

    /**
     * 获取容量label
     * @param capacity 容量（SECTOR）
     */
    getCapLabel = (capacity: number): string => this.getValueAndUnit(capacity).label;

    /**
     * 已分配容量渲染
     */
    usedCapacityRender = (usedCapacity: string, totalCapacity: string): string => this.get('common.allocatedandTotal_tip', [
        this.utilService.adaptUnit(usedCapacity, CAPACITY_UNIT.SECTOR).label,
        this.utilService.adaptUnit(totalCapacity, CAPACITY_UNIT.SECTOR).label,
    ]);

    /**
     * 未使用容量渲染
     */
    freeCapacityRender = (oriFreeCapacity: string | number): string => {
        let freeCapacity = oriFreeCapacity;
        freeCapacity = Math.max(0, Number(freeCapacity));
        return this.get('common.unused_tip', [
            this.utilService.adaptUnit(freeCapacity, CAPACITY_UNIT.SECTOR).label,
        ]);
    };

    /**
     * 固定长度错误提示
     */
    fixLengthErrMsg(length): string {
        const lengthStr = String(length);
        return this.get('common.length_character_valid', [lengthStr]);
    }

    /**
     * 必填报错msg
     */
    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    /**
     * 范围报错msg
     * @param min 范围最小值
     * @param max 范围最大值
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    /**
     * 通用名称报错msg
     */
    commonNameErr(name: string): string {
        return this.get('common.nameformat_validation', [name]);
    }

    /**
     * 删除LUN rest msg
     */
    deleteResult(name: string): string {
        return this.get('rest.deleteLun_msg1', [name]);
    }


    /**
     * 微存储混闪缓存策略有效期换算
     */
    leftTime = (leftHours: string | number): string => {
        if (!leftHours) {
            return UNPLACEHOLDER;
        }

        if (leftHours === CACHE_POLICY_INVALID_TIME_32BITS || leftHours === CACHE_POLICY_INVALID_TIME) {

            //无限期
            return this.get('service.filesystem_task_create_worm_para_protectTime_value_withoutday_checkbox');
        }

        const leftDay = Math.floor(Number(leftHours) / 24);
        const leftHour = Number(leftHours) % 24;
        return this.get('common.validity_dayAndHour_value', [leftDay, leftHour]);
    };
}
